import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const HowToUse = () => {
  const { language } = useLanguage();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const content = {
    'en': {
      title: 'How to Use Beat Master',
      subtitle: 'Master your rhythm with our comprehensive guide',
      sections: [
        {
          id: 'getting-started',
          title: 'Getting Started',
          icon: FiIcons.FiPlay,
          steps: [
            {
              title: 'Set Your Tempo',
              description: 'Use the tempo slider or tap button to set your desired beats per minute (BPM). The tap button allows you to tap at your desired rhythm to automatically calculate the BPM.',
              icon: FiIcons.FiClock,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: 'For beginners, start with a slower tempo (60-80 BPM) and gradually increase as you become more comfortable.'
            },
            {
              title: 'Choose Time Signature',
              description: 'Select your desired time signature from the dropdown menu. Beat Master supports common signatures like 4/4, 3/4, 6/8 and many more.',
              icon: FiIcons.FiGrid,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: '4/4 is the most common time signature and a good starting point if you\'re new to rhythm practice.'
            },
            {
              title: 'Start the Metronome',
              description: 'Press the large play button to start the metronome. The beat indicators will light up in sequence to provide visual feedback.',
              icon: FiIcons.FiPlay,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: 'Try to synchronize your movements or instrument with both the sound and visual cues for better timing.'
            }
          ]
        },
        {
          id: 'advanced-features',
          title: 'Advanced Features',
          icon: FiIcons.FiSettings,
          steps: [
            {
              title: 'Setting Beat Accents',
              description: 'Click on individual beats in the beat indicator section to toggle accents. Accented beats will play with a different sound to help emphasize specific beats in your measure.',
              icon: FiIcons.FiZap,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: 'In 4/4 time, accenting beat 1 (and sometimes beat 3) helps establish the downbeat feeling.'
            },
            {
              title: 'Adjusting Sound Options',
              description: 'Choose from different sound options like woodblock, click, drum, or bell. Adjust the volume to suit your practice environment.',
              icon: FiIcons.FiVolume2,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: 'The woodblock sound tends to cut through other instruments well, while the click is more subtle for quiet practice.'
            },
            {
              title: 'Using Subdivisions',
              description: 'Select different note types to practice with subdivisions. Options include quarter notes, eighth notes, triplets, and sixteenth notes.',
              icon: FiIcons.FiLayers,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: 'Practicing with subdivisions helps develop a more precise sense of timing within each beat.'
            }
          ]
        },
        {
          id: 'practice-tips',
          title: 'Practice Tips',
          icon: FiIcons.FiTarget,
          steps: [
            {
              title: 'Start Slow',
              description: 'Begin practicing at a slower tempo than your target speed. Gradually increase as you become more comfortable.',
              icon: FiIcons.FiTrendingUp,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: 'It\'s better to play perfectly at a slower tempo than to struggle at a faster one. Speed comes with consistency.'
            },
            {
              title: 'Use Visual Cues',
              description: 'Watch the beat indicators to improve your visual timing recognition alongside auditory cues.',
              icon: FiIcons.FiEye,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: 'Try practicing while looking away from the metronome occasionally to test your internal timing.'
            },
            {
              title: 'Practice Consistently',
              description: 'Regular practice with a metronome is key to improving your timing and rhythm. Even 10 minutes daily can make a significant difference.',
              icon: FiIcons.FiCalendar,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: 'Set specific rhythm goals for each practice session, such as maintaining perfect timing for a particular passage.'
            }
          ]
        }
      ],
      conclusion: {
        title: 'Mastering Rhythm Takes Time',
        content: 'Remember that developing a strong sense of rhythm is a journey. Use Beat Master regularly, be patient with yourself, and you\'ll notice significant improvements in your musical timing and confidence.',
        cta: 'Start practicing with Beat Master today!'
      }
    },
    'zh-TW': {
      title: 'Beat Master 使用指南',
      subtitle: '透過我們的完整指南掌握您的節奏感',
      sections: [
        {
          id: 'getting-started',
          title: '開始使用',
          icon: FiIcons.FiPlay,
          steps: [
            {
              title: '設定速度',
              description: '使用速度滑桿或點擊按鈕來設定您想要的每分鐘拍數（BPM）。點擊按鈕可以讓您按照您想要的節奏輕敲，自動計算BPM。',
              icon: FiIcons.FiClock,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: '對於初學者，建議從較慢的速度開始（60-80 BPM），隨著您越來越熟練逐漸提高。'
            },
            {
              title: '選擇拍號',
              description: '從下拉菜單中選擇您需要的拍號。Beat Master 支持常見的拍號如 4/4、3/4、6/8 等多種拍號。',
              icon: FiIcons.FiGrid,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: '4/4 是最常見的拍號，如果您是節奏練習的新手，這是一個很好的起點。'
            },
            {
              title: '啟動節拍器',
              description: '按下大型播放按鈕開始節拍器。拍子指示器將依序亮起，提供視覺反饋。',
              icon: FiIcons.FiPlay,
              iconBg: 'bg-blue-100 dark:bg-blue-900/30',
              iconColor: 'text-blue-600 dark:text-blue-400',
              tip: '嘗試將您的動作或樂器與聲音和視覺提示同步，以獲得更好的時間感。'
            }
          ]
        },
        {
          id: 'advanced-features',
          title: '進階功能',
          icon: FiIcons.FiSettings,
          steps: [
            {
              title: '設置重音拍',
              description: '點擊拍子指示器部分中的各個拍子來切換重音。重音拍會播放不同的聲音，幫助強調小節中的特定拍子。',
              icon: FiIcons.FiZap,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: '在 4/4 拍中，強調第 1 拍（有時也強調第 3 拍）有助於建立強拍感。'
            },
            {
              title: '調整聲音選項',
              description: '從木魚、點擊聲、鼓聲或鈴聲等不同聲音選項中選擇。調整音量以適應您的練習環境。',
              icon: FiIcons.FiVolume2,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: '木魚聲音通常能夠穿透其他樂器的聲音，而點擊聲則更為微妙，適合安靜的練習環境。'
            },
            {
              title: '使用細分音符',
              description: '選擇不同的音符類型來練習細分。選項包括四分音符、八分音符、三連音和十六分音符。',
              icon: FiIcons.FiLayers,
              iconBg: 'bg-purple-100 dark:bg-purple-900/30',
              iconColor: 'text-purple-600 dark:text-purple-400',
              tip: '練習細分音符有助於在每個節拍內培養更精確的時間感。'
            }
          ]
        },
        {
          id: 'practice-tips',
          title: '練習技巧',
          icon: FiIcons.FiTarget,
          steps: [
            {
              title: '從慢速開始',
              description: '以低於目標速度的節奏開始練習。隨著您變得更加熟練，逐漸提高速度。',
              icon: FiIcons.FiTrendingUp,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: '在較慢的速度上完美演奏比在較快的速度上掙扎要好。速度來自於一致性。'
            },
            {
              title: '利用視覺提示',
              description: '觀察拍子指示器，與聽覺提示一起提高您的視覺節奏感知。',
              icon: FiIcons.FiEye,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: '嘗試偶爾不看節拍器進行練習，以測試您的內在時間感。'
            },
            {
              title: '持續練習',
              description: '使用節拍器進行規律練習是提高您的節奏感和時間感的關鍵。即使每天只有10分鐘，也能產生顯著的差異。',
              icon: FiIcons.FiCalendar,
              iconBg: 'bg-pink-100 dark:bg-pink-900/30',
              iconColor: 'text-pink-600 dark:text-pink-400',
              tip: '為每次練習設定特定的節奏目標，例如在特定段落中保持完美的時間感。'
            }
          ]
        }
      ],
      conclusion: {
        title: '掌握節奏需要時間',
        content: '請記住，培養強烈的節奏感是一個過程。定期使用 Beat Master，對自己保持耐心，您會發現您的音樂時間感和信心有顯著提升。',
        cta: '立即開始使用 Beat Master 進行練習！'
      }
    }
  };

  const currentContent = content[language] || content['en'];

  // Animation variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {currentContent.title}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          {currentContent.subtitle}
        </p>
      </motion.div>

      <motion.div 
        className="space-y-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {currentContent.sections.map((section, sectionIndex) => {
          // Get color scheme based on section index
          const colorScheme = sectionIndex === 0 
            ? { light: 'blue', dark: 'blue', bg: 'bg-blue-100 dark:bg-blue-800/20', text: 'text-blue-600 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-700', shadow: 'shadow-blue-100 dark:shadow-blue-900/10' }
            : sectionIndex === 1
            ? { light: 'purple', dark: 'purple', bg: 'bg-purple-100 dark:bg-purple-800/20', text: 'text-purple-600 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-700', shadow: 'shadow-purple-100 dark:shadow-purple-900/10' }
            : { light: 'pink', dark: 'pink', bg: 'bg-pink-100 dark:bg-pink-800/20', text: 'text-pink-600 dark:text-pink-300', border: 'border-pink-200 dark:border-pink-700', shadow: 'shadow-pink-100 dark:shadow-pink-900/10' };

          return (
            <motion.section 
              key={section.id} 
              id={section.id}
              className="scroll-mt-20 relative"
              variants={sectionVariants}
            >
              {/* Section header with enhanced styling */}
              <div className={`flex items-center mb-8 px-5 py-3 ${colorScheme.bg} rounded-lg border ${colorScheme.border}`}>
                <div className={`p-3 rounded-full bg-white dark:bg-gray-800 ${colorScheme.text} mr-4 shadow-md ${colorScheme.shadow}`}>
                  <SafeIcon icon={section.icon} className="h-6 w-6" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                  {section.title}
                </h2>
              </div>

              {/* Steps with improved mobile-friendly layout */}
              <div className="space-y-12">
                {section.steps.map((step, stepIndex) => {
                  // Generate a unique gradient for each step based on the section color
                  const gradientClass = `bg-gradient-to-br from-${colorScheme.light}-50 to-${colorScheme.light}-100 dark:from-${colorScheme.dark}-900/30 dark:to-${colorScheme.dark}-800/20`;
                  
                  return (
                    <motion.div 
                      key={stepIndex} 
                      className="relative rounded-xl overflow-hidden"
                      variants={stepVariants}
                    >
                      {/* Card with improved mobile layout */}
                      <div className={`p-6 md:p-8 ${gradientClass} border ${colorScheme.border} rounded-xl shadow-sm`}>
                        <div className="flex flex-col md:flex-row md:gap-8">
                          
                          {/* Content Section */}
                          <div className="md:w-3/5">
                            <div className="flex items-center mb-4">
                              <div className={`flex-shrink-0 w-10 h-10 ${colorScheme.bg} rounded-full flex items-center justify-center mr-3`}>
                                <SafeIcon icon={step.icon} className={`h-5 w-5 ${colorScheme.text}`} />
                              </div>
                              <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                                {step.title}
                              </h3>
                            </div>
                            
                            <p className="text-gray-700 dark:text-gray-300 mb-4">
                              {step.description}
                            </p>
                            
                            {/* Tip box with improved styling */}
                            <div className="bg-white/70 dark:bg-gray-800/50 border-l-4 border-amber-500 dark:border-amber-600 p-3 rounded-r mt-4">
                              <div className="flex">
                                <SafeIcon icon={FiIcons.FiInfo} className="text-amber-600 dark:text-amber-500 mt-1 mr-2 flex-shrink-0" />
                                <p className="text-sm text-amber-800 dark:text-amber-300">
                                  <span className="font-semibold">{language === 'zh-TW' ? '提示：' : 'Tip: '}</span>
                                  {step.tip}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Visual Section - More attractive and responsive */}
                          <div className="md:w-2/5 mt-6 md:mt-0">
                            <div className="rounded-lg overflow-hidden h-full">
                              <div className={`h-full flex items-center justify-center p-6 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border ${colorScheme.border} rounded-xl`}>
                                <div className="text-center">
                                  {/* Larger, more attractive icon */}
                                  <div className={`mx-auto w-20 h-20 ${colorScheme.bg} rounded-full flex items-center justify-center mb-4 shadow-lg ${colorScheme.shadow}`}>
                                    <SafeIcon icon={step.icon} className={`h-10 w-10 ${colorScheme.text}`} />
                                  </div>
                                  
                                  {/* Step number indicator */}
                                  <div className={`inline-block px-3 py-1 rounded-full ${colorScheme.bg} ${colorScheme.text} text-sm font-medium mb-2`}>
                                    {language === 'zh-TW' ? '步驟' : 'Step'} {stepIndex + 1}
                                  </div>
                                  
                                  <div className="font-medium text-gray-800 dark:text-gray-200 text-lg">
                                    {step.title}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.section>
          );
        })}

        {/* Conclusion with improved styling */}
        <motion.div 
          className="mt-16 relative"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <div className="p-8 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl text-center shadow-md border border-purple-100 dark:border-purple-800/30">
            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-700/30 opacity-70"></div>
            <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-pink-200 dark:bg-pink-700/30 opacity-70"></div>
            
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 relative z-10">
              {currentContent.conclusion.title}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              {currentContent.conclusion.content}
            </p>
            
            {/* Enhanced CTA button */}
            <Link to="/" className="inline-block">
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-200"></div>
                <button className="relative px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiIcons.FiPlay} className="h-5 w-5" />
                    <span>{currentContent.conclusion.cta}</span>
                  </div>
                </button>
              </div>
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HowToUse;